import React, { useState, useEffect } from 'react';
import { Save, Image, Check, Eye, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { BackgroundConfig, UpdateVfxConfigRequest } from '../../../types';
import { useUpdateVfxConfig } from '../../../hooks/useApi';

interface BackgroundConfigFormProps {
  config: BackgroundConfig | null;
  onSuccess?: () => void;
  scheduleId?: string;
  onScheduleUpdate?: (data: UpdateVfxConfigRequest) => Promise<void>;
  /** When saving via parent (e.g. schedule), pass true while API is in progress so button shows loading */
  externalSaving?: boolean;
}

const THEME_NAMES: Record<number, { name: string; description: string }> = {
  1: { name: 'Christmas Magic', description: 'Santa, reindeer, and festive decorations' },
  2: { name: 'New Year Celebration', description: 'Champagne, fireworks, and party vibes' },
  3: { name: 'Valentine Romance', description: 'Hearts, roses, and love in the air' },
  4: { name: 'St Patrick Luck', description: 'Shamrocks, gold coins, and Irish charm' },
  5: { name: 'Spring Bloom', description: 'Flowers, butterflies, and spring awakening' },
  6: { name: 'Summer Vibes', description: 'Beach, sun, and tropical paradise' },
  7: { name: 'Independence Day', description: 'American flag, fireworks, and patriotic spirit' },
  8: { name: 'Halloween Spooky', description: 'Pumpkins, ghosts, and spooky fun' },
  9: { name: 'Thanksgiving Harvest', description: 'Autumn leaves, pumpkins, and harvest bounty' },
  10: { name: 'Winter Wonderland', description: 'Snowman, snowflakes, and cozy winter' },
};

const THEMES = Array.from({ length: 10 }, (_, i) => i + 1);

const BackgroundConfigForm: React.FC<BackgroundConfigFormProps> = ({ config, onSuccess, scheduleId, onScheduleUpdate, externalSaving = false }) => {
  const [formData, setFormData] = useState<BackgroundConfig>({
    enabled: false,
    url: null,
    fallback: '#a1a1d4',
    theme: 0,
  });
  const [previewTheme, setPreviewTheme] = useState<number | null>(null);

  const updateConfig = useUpdateVfxConfig();

  useEffect(() => {
    if (config) {
      setFormData({
        enabled: config.enabled,
        url: config.url,
        fallback: config.fallback || '#a1a1d4',
        theme: config.theme || 0,
      });
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updateData: UpdateVfxConfigRequest = {
      background: {
        enabled: formData.enabled,
        fallback: formData.fallback,
        theme: formData.theme || 0,
      },
    };

    try {
      if (scheduleId && onScheduleUpdate) {
        await onScheduleUpdate(updateData);
      } else {
      await updateConfig.mutateAsync(updateData);
      }
      onSuccess?.();
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleToggle = (enabled: boolean) => {
    setFormData({
      ...formData,
      enabled,
      theme: enabled ? (formData.theme || 0) : 0,
    });
  };

  const handleThemeSelect = (theme: number) => {
    setFormData({
      ...formData,
      theme: formData.theme === theme ? 0 : theme,
    });
  };

  if (!config) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Image className="h-5 w-5 mr-2" />
          Background Configuration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <label className="text-sm font-medium">Enable Background Image</label>
              <p className="text-xs text-muted-foreground mt-1">
                Toggle to enable or disable background image on client websites
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {formData.enabled && (
            <>
              {/* Theme Selection */}
              <div>
                <label className="block text-sm font-medium mb-3">
                  Select Theme
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {THEMES.map((themeNum) => {
                    const isSelected = formData.theme === themeNum;
                    return (
                      <div
                        key={themeNum}
                        className={`relative group border-2 rounded-lg overflow-hidden transition-all ${
                          isSelected
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="aspect-video bg-gray-100 relative">
                          <img
                            src={`/imgs/theme${themeNum}.png`}
                            alt={`Theme ${themeNum}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center pointer-events-none">
                              <Check className="h-6 w-6 text-blue-600" />
                            </div>
                          )}
                          {/* Preview Button - Top Right Corner */}
                          <div className="absolute top-2 right-2 z-30">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setPreviewTheme(themeNum);
                              }}
                              className="bg-white/90 hover:bg-white shadow-md"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="p-2 bg-white">
                          <button
                            type="button"
                            onClick={() => handleThemeSelect(themeNum)}
                            className="w-full text-left"
                          >
                            <p className="text-xs font-medium text-center">{THEME_NAMES[themeNum]?.name || `Theme ${themeNum}`}</p>
                            <p className="text-xs text-muted-foreground text-center mt-0.5 line-clamp-1">
                              {THEME_NAMES[themeNum]?.description || ''}
                            </p>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {formData.theme === 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    No theme selected. Please select a theme to enable background.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              type="submit"
              disabled={updateConfig.isLoading || externalSaving}
              className="flex items-center space-x-2"
            >
              {updateConfig.isLoading || externalSaving ? (
                <>
                  <LoadingSpinner />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Background Configuration</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>

      {/* Preview Modal */}
      {previewTheme !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-75 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4 sm:p-6"
          onClick={() => setPreviewTheme(null)}
        >
          <div className="min-h-full flex items-start justify-center">
            <div className="relative w-full max-w-4xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-xl font-semibold">
                    {THEME_NAMES[previewTheme]?.name || `Theme ${previewTheme}`}
                  </CardTitle>
                  {THEME_NAMES[previewTheme]?.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {THEME_NAMES[previewTheme].description}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewTheme(null)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="relative w-full rounded-lg border overflow-hidden bg-gray-100" style={{ aspectRatio: '16/9' }}>
                  <img
                    src={`/imgs/theme${previewTheme}.png`}
                    alt={`${THEME_NAMES[previewTheme]?.name || `Theme ${previewTheme}`} preview`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewTheme(null)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      handleThemeSelect(previewTheme);
                      setPreviewTheme(null);
                    }}
                  >
                    Select This Theme
                  </Button>
                </div>
              </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BackgroundConfigForm;

